import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import Button from "@mui/material/Button";
import NoFailedTest from "./NoFailedTest";

import { RootContext } from "../../../context/RootContext";

const FAILED_TESTS_PREVIEW_COUNT = 3;

const LoadMoreDivider = ({
  expanded,
  totalCount,
  onClick,
  t,
}: {
  expanded: boolean;
  totalCount: number;
  onClick: () => void;
  t: any;
}) => {
  if (expanded) {
    return (
      <div
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="load-more-tests-button"
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          margin: "12px 0",
        }}
      >
        <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />
        <span
          style={{
            padding: "0 12px",
            color: "#3c3c3c",
            fontWeight: 600,
            fontSize: "14px",
            whiteSpace: "nowrap",
          }}
        >
          {t("allResultsShown", { count: totalCount })}
        </span>
        <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        margin: "12px 0",
        alignItems: "center",
      }}
    >
      <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />&ensp;
      <Button
        variant="outlined"
        onClick={onClick}
        data-testid="load-more-tests-button"
        sx={{
          borderRadius: "20px",
          textTransform: "none",
          borderColor: "#B8B8C7",
          color: "#3c3c3c",
          fontWeight: 600,
          fontSize: "14px",
          paddingX: 3,
        }}
      >
        {t("loadMoreCount", {
          count: totalCount - FAILED_TESTS_PREVIEW_COUNT,
        })}
      </Button>
      &ensp;
      <span style={{ flex: 1, height: "1px", background: "#B8B8C7" }} />
    </div>
  );
};

const contentDiv = {
  padding: "12px",
  borderRadius: "6px",
  background: "white",
};

const countCriticalFailures = (obj: any) => {
  // Get the first key of the object and count the number of critical failures
  const key = Object.keys(obj)[0];
  return obj[key].filter((item: any) => item.isCriticalTest).length;
};

const CollapsedCardHeader = (opts: {
  test: any;
  currentData: any;
  criticalFailedTests: any;
  nonCriticalFailedTests: any;
  waiverProps: any;
}) => {
  const { test, currentData, criticalFailedTests, nonCriticalFailedTests, waiverProps } = opts;
  const {
    showRadioSelection,
    loanDetails,
    eligiblePartnerResults,
    changePartner,
    isApplicationSubmitted,
    CustomChip,
    CriticalTestIcon,
    NonCriticalTestIcon,
    CheckCirclePass,
  } = waiverProps;

  return (
    <Grid sx={{
      padding: "12px",
      // background: "#E4EAFFCC",
      // border: "1px solid #DBE2FF",
      // fontSize: "18px",
      // fontWeight: 600,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    }}>
      {showRadioSelection ? <Radio
        checked={
          [loanDetails?.takeOutPartner].includes(currentData?.id) &&
          eligiblePartnerResults?.dailyCreditLimit?.[currentData?.id]?.isWithinLimit
        }
        disabled={!eligiblePartnerResults?.dailyCreditLimit?.[currentData?.id]?.isWithinLimit || isApplicationSubmitted}
        onChange={(e) => changePartner(currentData)}
      /> : null}
      <span>{Object.keys(test)[0]}</span>&ensp;
      {criticalFailedTests ? <CustomChip
        size="small"
        label={
          <div className="div-space-between">
            <img
              src={CriticalTestIcon}
              alt="info-icon"
              className="mr-8"
            />
            {`Critical Fails - ${criticalFailedTests}`}
          </div>
        }
        variant="outlined"
        className="critical-failed-chip"
      /> : null}
      {nonCriticalFailedTests ? <CustomChip
        size="small"
        label={
          <div className="div-space-between">
            <img
              src={NonCriticalTestIcon}
              alt="info-icon"
              className="mr-8"
            />
            {`Non-critical Fails - ${nonCriticalFailedTests}`}
          </div>
        }
        variant="outlined"
        className="non-critical-chip-data"
      /> : null}
      {!nonCriticalFailedTests && !criticalFailedTests ?
        <img
          src={CheckCirclePass}
          alt="check-pass-circle"
          className="mr-8"
        /> : null}
    </Grid>
  );
};

const CollapsedTestCard = (opts: {
  test: any;
  idx: number;
  gridSize: number;
  isLoanDSCR: any;
  currentTabKey: string;
  currentData: any;
  criticalFailedTests: any;
  nonCriticalFailedTests: any;
  waiverProps: any;
  cardCommonProps: any;
}) => {
  const {
    test,
    idx,
    gridSize,
    isLoanDSCR,
    currentTabKey,
    currentData,
    criticalFailedTests,
    nonCriticalFailedTests,
    waiverProps,
    cardCommonProps,
  } = opts;
  const {
    loanDetails,
    CriticalTestIcon,
    NonCriticalTestIcon,
    showAllFailures,
    toggleShowAllFailures,
    t,
  } = waiverProps;
  const { CustomCard, ratesAsOfDate, canViewWaiver, canRequestWaiver, canActOnWaiver, onRequestWaiverClick, finalDataToBeDisplayed, openWaiverKey, getWaiverKey, handleWaiverSubmit, handleWaiverCancel } = cardCommonProps;

  return (
    <Grid item xs={gridSize} key={idx}>
      {isLoanDSCR ? (
        <div style={{
          padding: "12px",
          background: "#E4EAFFCC",
          border: "1px solid #DBE2FF",
          fontSize: "18px",
          fontWeight: 600,
          minHeight: "100px"
          }}>
          <CollapsedCardHeader
            test={test}
            currentData={currentData}
            criticalFailedTests={criticalFailedTests}
            nonCriticalFailedTests={nonCriticalFailedTests}
            waiverProps={waiverProps}
          />
          <Grid
            style={
              test[Object.keys(test)[0]]?.length
                ? { background: "white", padding: 16, minHeight: "90%" }
                : {
                  background: "white",
                  padding: 16,
                  minHeight: "90%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }
            }
          >
            {(showAllFailures[currentTabKey]
              ? test[Object.keys(test)[0]]
              : test[Object.keys(test)[0]]?.slice(0, FAILED_TESTS_PREVIEW_COUNT)
            )?.map(
              (testEle: any, idx: number) => {
                if (testEle) {
                  return (
                    <CustomCard
                      key={idx}
                      headingText={testEle.title}
                      subHeadingText={testEle.subText}
                      loanDetails={loanDetails}
                      fullView={true}
                      ratesAsOfDate={ratesAsOfDate}
                      gridSpace={testEle?.gridSpace ?? 4}
                      showFailed={true}
                      icon={testEle?.isCriticalTest ? CriticalTestIcon : NonCriticalTestIcon}
                      showRequestWaiver={canViewWaiver}
                      canRequestWaiver={canRequestWaiver}
                      canActOnWaiver={canActOnWaiver}
                      onRequestWaiverClick={onRequestWaiverClick}
                      currentTabResults={finalDataToBeDisplayed}
                      currentTabKey={currentTabKey}
                      testType={testEle?.testType}
                      testKey={testEle?.testKey}
                      isWaiverFormOpen={
                        openWaiverKey === getWaiverKey(currentTabKey, testEle.title)
                      }
                      onWaiverSubmit={handleWaiverSubmit}
                      onWaiverCancel={handleWaiverCancel}
                    />
                  );
                }
              }
            )}
            {test[Object.keys(test)[0]]?.length > FAILED_TESTS_PREVIEW_COUNT && (
              <LoadMoreDivider
                expanded={!!showAllFailures[currentTabKey]}
                totalCount={test[Object.keys(test)[0]].length}
                onClick={() => toggleShowAllFailures(currentTabKey)}
                t={t}
              />
            )}
            {!test[Object.keys(test)[0]]?.length ? <NoFailedTest /> : null}
          </Grid>
        </div>
      ) : (
        <CustomCard
          key={idx}
          headingText={test.title}
          subHeadingText={test.subText}
          loanDetails={loanDetails}
          fullView={true}
          ratesAsOfDate={ratesAsOfDate}
          gridSpace={test?.gridSpace ?? 4}
          showFailed={true}
          showRequestWaiver={canViewWaiver}
          canRequestWaiver={canRequestWaiver}
          canActOnWaiver={canActOnWaiver}
          onRequestWaiverClick={onRequestWaiverClick}
          currentTabResults={finalDataToBeDisplayed}
          currentTabKey={currentTabKey}
          testType={test?.testType}
          testKey={test?.testKey}
          isWaiverFormOpen={
            openWaiverKey === getWaiverKey(currentTabKey, test.title)
          }
          onWaiverSubmit={handleWaiverSubmit}
          onWaiverCancel={handleWaiverCancel}
        />
      )}
    </Grid>
  );
};

const ExpandedTestColumn = (opts: {
  test: any;
  idx: number;
  gridSize: number;
  isLoanDSCR: any;
  currentTabKey: string;
  waiverProps: any;
  cardCommonProps: any;
}) => {
  const { test, idx, gridSize, isLoanDSCR, currentTabKey, waiverProps, cardCommonProps } = opts;
  const { InfoIcon, CustomChip, showAllFailures, toggleShowAllFailures, t } = waiverProps;
  const { CustomCard, loanDetails, ratesAsOfDate, canViewWaiver, canRequestWaiver, canActOnWaiver, onRequestWaiverClick, finalDataToBeDisplayed, openWaiverKey, getWaiverKey, handleWaiverSubmit, handleWaiverCancel } = cardCommonProps;

  return (
    <Grid
      item
      xs={gridSize <= 4 ? 5 : gridSize}
      style={{
        borderRight: "1px solid #A9A9A9",
        minWidth: "400px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "6px 0px 0px 0px",
          border: "1px solid #DBE2FF",
          background: "rgba(228, 234, 255, 0.80)",
          padding: "0px 10px",
        }}
      >
        <h3>{isLoanDSCR ? currentTabKey : test.title}</h3>
        <CustomChip
          size="small"
          label={
            <div className="div-space-between">
              <img src={InfoIcon} alt="info-icon" className="mr-8" />{" "}
              {isLoanDSCR
                ? `Failed - ${test[currentTabKey]?.length}`
                : "Failed"}
            </div>
          }
          variant="outlined"
          className={test[currentTabKey]?.length ? "error-chipData" : "success-chipData"}
          data-testid="custom-chip"
        />
      </div>
      <div>
        {isLoanDSCR ? (
          <>
            {(showAllFailures[currentTabKey]
              ? test[currentTabKey]
              : test[currentTabKey]?.slice(0, FAILED_TESTS_PREVIEW_COUNT)
            )?.map(
              (testEle: any, idx: number) => {
                return (
                  <div key={idx} style={contentDiv}>
                    <CustomCard
                      headingText={testEle.title}
                      subHeadingText={testEle.subText}
                      loanDetails={loanDetails}
                      fullView={true}
                      ratesAsOfDate={ratesAsOfDate}
                      gridSpace={testEle?.gridSpace ?? 4}
                      cardCss={true}
                      showRequestWaiver={canViewWaiver}
                      canRequestWaiver={canRequestWaiver}
                      canActOnWaiver={canActOnWaiver}
                      onRequestWaiverClick={onRequestWaiverClick}
                      currentTabKey={currentTabKey}
                      testType={testEle?.testType}
                      testKey={testEle?.testKey}
                      isWaiverFormOpen={
                        openWaiverKey === getWaiverKey(currentTabKey, testEle.title)
                      }
                      onWaiverSubmit={handleWaiverSubmit}
                      onWaiverCancel={handleWaiverCancel}
                    />
                  </div>
                );
              }
            )}
            {test[currentTabKey]?.length > FAILED_TESTS_PREVIEW_COUNT && (
              <LoadMoreDivider
                expanded={!!showAllFailures[currentTabKey]}
                totalCount={test[currentTabKey].length}
                onClick={() => toggleShowAllFailures(currentTabKey)}
                t={t}
              />
            )}
          </>
        ) : (
          <CustomCard
            key={idx}
            headingText={test.title}
            subHeadingText={test.subText}
            loanDetails={loanDetails}
            fullView={true}
            ratesAsOfDate={ratesAsOfDate}
            gridSpace={test?.gridSpace ?? 4}
            showFailed={true}
            showRequestWaiver={canViewWaiver}
            canRequestWaiver={canRequestWaiver}
            canActOnWaiver={canActOnWaiver}
            onRequestWaiverClick={onRequestWaiverClick}
            currentTabResults={finalDataToBeDisplayed}
            currentTabKey={currentTabKey}
            testType={test?.testType}
            testKey={test?.testKey}
            isWaiverFormOpen={
              openWaiverKey === getWaiverKey(currentTabKey, test.title)
            }
            onWaiverSubmit={handleWaiverSubmit}
            onWaiverCancel={handleWaiverCancel}
          />
        )}
      </div>
    </Grid>
  );
};

export default function TestTabResultView({
  tabData,
  isExpanded,
  expandLeverageAndTests,
  setIsExpanded,
  loanDetails,
  eligiblePartnerResults,
  changePartner,
  isLoanDSCR,
  ratesAsOfDate,
  CustomCard,
  CustomChip,
  DSCROrgPartnerMapping,
  DownArrow,
  InfoIcon,
  NonCriticalTestIcon,
  CriticalTestIcon,
  isApplicationSubmitted,
  CheckCirclePass,
  showRadioSelection,
}: {
  tabData: any;
  isExpanded: any;
  expandLeverageAndTests: boolean;
  setIsExpanded: any;
  loanDetails: any;
  eligiblePartnerResults: any;
  changePartner: any;
  CustomCard: any;
  CustomChip: any;
  DSCROrgPartnerMapping: any;
  DownArrow: any;
  InfoIcon: any;
  isLoanDSCR?: boolean;
  ratesAsOfDate?: string;
  NonCriticalTestIcon?: any,
  CriticalTestIcon?: any
  isApplicationSubmitted?: boolean;
  CheckCirclePass?: any;
  showRadioSelection?: boolean;
}) {
  const { t } = useTranslation();
  const { dynamicConfig } = useContext(RootContext) ?? {};
  const canRequestWaiver = !!dynamicConfig?.showRequestWaiver;
  const canActOnWaiver = !!dynamicConfig?.showRequestWaiver;
  const canViewWaiver = !!dynamicConfig?.showRequestWaiver;

  const [showAllFailures, setShowAllFailures] = useState<
    Record<string, boolean>
  >({});

  const toggleShowAllFailures = (programKey: string) => {
    setShowAllFailures((prev) => ({
      ...prev,
      [programKey]: !prev[programKey],
    }));
  };

  const [openWaiverKey, setOpenWaiverKey] = useState<string | null>(null);

  const getWaiverKey = (tabKey: string, testTitle: string) =>
    `${tabKey}__${testTitle}`;

  const onRequestWaiverClick = (props: any): void => {
    const key = getWaiverKey(props?.currentTabKey, props?.headingText);
    setOpenWaiverKey((prev) => (prev === key ? null : key));
  };

  const handleWaiverSubmit = () => {
    setOpenWaiverKey(null);
  };

  const handleWaiverCancel = () => {
    setOpenWaiverKey(null);
  };

  const gridSize = Math.floor(12 / tabData?.length);
  const finalDataToBeDisplayed = tabData.sort(
    (a: any, b: any) => countCriticalFailures(a) - countCriticalFailures(b)
  );

  const waiverProps = {
    showRadioSelection,
    loanDetails,
    eligiblePartnerResults,
    changePartner,
    isApplicationSubmitted,
    CustomChip,
    CriticalTestIcon,
    NonCriticalTestIcon,
    CheckCirclePass,
    showAllFailures,
    toggleShowAllFailures,
    InfoIcon,
    t,
  };

  const cardCommonProps = {
    CustomCard,
    loanDetails,
    ratesAsOfDate,
    canViewWaiver,
    canRequestWaiver,
    canActOnWaiver,
    onRequestWaiverClick,
    finalDataToBeDisplayed,
    openWaiverKey,
    getWaiverKey,
    handleWaiverSubmit,
    handleWaiverCancel,
  };

  return (
    <div>
      <Grid
        container
        spacing={expandLeverageAndTests ? 0 : 1}
        className="mt-12"
        style={
          expandLeverageAndTests
            ? { overflowX: "auto", flexWrap: "nowrap" }
            : {}
        }
      >
        {finalDataToBeDisplayed?.map((test: any, idx: number) => {
          const criticalFailedTests: any = test[Object.keys(test)[0]]?.filter((test: any) => test?.isCriticalTest)?.length;
          const nonCriticalFailedTests: any = test[Object.keys(test)[0]]?.filter((test: any) => !test?.isCriticalTest)?.length;

          const currentTabKey = Object.keys(test)[0];
          const currentId = Object.keys(DSCROrgPartnerMapping).find((key: string) => DSCROrgPartnerMapping[key] === currentTabKey);
          const currentData: any = {
            title: currentTabKey,
            id: currentId
          };

          return !expandLeverageAndTests ? (
            <CollapsedTestCard
              key={idx}
              test={test}
              idx={idx}
              gridSize={gridSize}
              isLoanDSCR={isLoanDSCR}
              currentTabKey={currentTabKey}
              currentData={currentData}
              criticalFailedTests={criticalFailedTests}
              nonCriticalFailedTests={nonCriticalFailedTests}
              waiverProps={waiverProps}
              cardCommonProps={cardCommonProps}
            />
          ) : (
            <ExpandedTestColumn
              key={idx}
              test={test}
              idx={idx}
              gridSize={gridSize}
              isLoanDSCR={isLoanDSCR}
              currentTabKey={currentTabKey}
              waiverProps={waiverProps}
              cardCommonProps={cardCommonProps}
            />
          );
        })}
      </Grid>
    </div>
  );
}
